<template>
  <transition name="modal">
    <div class="backdrop" v-if="visible" @click.self="close">
      <div class="panel" :class="{ fullscreen: isMobile }">

        <!-- Top bar -->
        <div class="topbar">
          <div class="topbar-left">
            <span class="dataset-badge">{{ datasetName }}</span>
            <span class="object-uid">{{ (modelData.uid || modelData.id || '').slice(0, 14) + '\u2026' }}</span>
          </div>
          <div class="topbar-actions">
            <!-- Nav arrows -->
            <button class="icon-btn" @click="$emit('prev')" :disabled="modelIndex <= 0" title="Previous (←)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <span class="nav-counter">{{ modelIndex + 1 }} / {{ totalModels }}</span>
            <button class="icon-btn" @click="$emit('next')" :disabled="modelIndex >= totalModels - 1" title="Next (→)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
            </button>
            <div class="topbar-sep"></div>
            <!-- Diff toggle -->
            <button class="icon-btn" :class="{ active: diffMode }" @click="diffMode = !diffMode" title="Compare two levels">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
            </button>
            <!-- Copy -->
            <button class="icon-btn" :class="{ success: copied }" @click="copyAnnotation" title="Copy annotation">
              <svg v-if="!copied" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>
            </button>
            <!-- Share -->
            <button class="icon-btn" :class="{ success: shared }" @click="shareLink" title="Copy share link">
              <svg v-if="!shared" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>
            </button>
            <div class="topbar-sep"></div>
            <button class="close-btn" @click="close" title="Close (Esc)">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        <!-- Object name -->
        <div class="object-header">
          <h2 class="object-name">{{ formatName(modelData.name) }}</h2>
          <div class="kbd-bar">
            <span><kbd>←</kbd><kbd>→</kbd> navigate</span>
            <span><kbd>1</kbd>–<kbd>5</kbd> level</span>
            <span v-if="diffMode"><kbd>D</kbd> diff</span>
          </div>
        </div>

        <!-- Body -->
        <div class="body">

          <!-- Viewer -->
          <div class="viewer-pane">
            <template v-if="type === 0">
              <div ref="viewerContainer" class="viewer-fill"></div>
              <transition name="fade">
                <div class="viewer-overlay" v-if="viewerLoading">
                  <div class="spinner"></div>
                  <span>Loading 3D viewer</span>
                </div>
              </transition>
            </template>
            <template v-else>
              <img :src="imageSrc" :alt="modelData.name" class="static-img"
                @error="e => e.target.src = 'https://placehold.co/600x400/0f1630/3d5af1?text=No+Image'"
              />
            </template>
          </div>

          <!-- Annotation pane: normal or diff -->
          <div class="anno-pane">

            <!-- ── NORMAL MODE ── -->
            <template v-if="!diffMode">
              <div class="level-header">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span>Annotation Level</span>
              </div>
              <div class="level-pills">
                <button
                  v-for="level in orderedLevels" :key="level"
                  class="level-pill" :class="{ active: selectedLevel === level }"
                  @click="selectedLevel = level"
                >
                  <span class="pill-num">L{{ level }}</span>
                  <span class="pill-label">{{ levelShort[level] }}</span>
                </button>
              </div>
              <div class="level-strip">
                <span class="strip-dot"></span>
                <span class="strip-text">{{ levelMeta[selectedLevel] }}</span>
              </div>
              <div class="anno-scroll">
                <transition name="slide" mode="out-in">
                  <div class="anno-text" :key="selectedLevel">
                    <p v-if="getText(selectedLevel)">{{ getText(selectedLevel) }}</p>
                    <p class="anno-empty" v-else>No annotation available for this level.</p>
                  </div>
                </transition>
              </div>
            </template>

            <!-- ── DIFF MODE ── -->
            <template v-else>
              <div class="level-header">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                <span>Compare Levels</span>
                <button class="diff-close" @click="diffMode = false">✕ Exit</button>
              </div>
              <div class="diff-selectors">
                <div class="diff-col">
                  <div class="diff-col-label">Level A</div>
                  <div class="level-pills">
                    <button v-for="l in orderedLevels" :key="l" class="level-pill sm" :class="{ active: diffLevelA === l }" @click="diffLevelA = l">
                      <span class="pill-num">L{{ l }}</span>
                      <span class="pill-label">{{ levelShort[l] }}</span>
                    </button>
                  </div>
                </div>
                <div class="diff-col">
                  <div class="diff-col-label">Level B</div>
                  <div class="level-pills">
                    <button v-for="l in orderedLevels" :key="l" class="level-pill sm" :class="{ active: diffLevelB === l }" @click="diffLevelB = l">
                      <span class="pill-num">L{{ l }}</span>
                      <span class="pill-label">{{ levelShort[l] }}</span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="diff-panels">
                <div class="diff-panel">
                  <div class="diff-panel-head">L{{ diffLevelA }} · {{ levelShort[diffLevelA] }}</div>
                  <div class="diff-panel-text">
                    <p v-if="getText(diffLevelA)">{{ getText(diffLevelA) }}</p>
                    <p class="anno-empty" v-else>No annotation.</p>
                  </div>
                </div>
                <div class="diff-divider"></div>
                <div class="diff-panel">
                  <div class="diff-panel-head">L{{ diffLevelB }} · {{ levelShort[diffLevelB] }}</div>
                  <div class="diff-panel-text">
                    <p v-if="getText(diffLevelB)">{{ getText(diffLevelB) }}</p>
                    <p class="anno-empty" v-else>No annotation.</p>
                  </div>
                </div>
              </div>
            </template>

            <!-- Footer stats (always shown) -->
            <div class="anno-footer">
              <div class="footer-stat">
                <span class="stat-label">Words</span>
                <span class="stat-val">{{ wordCount }}</span>
              </div>
              <div class="footer-stat">
                <span class="stat-label">Level</span>
                <span class="stat-val">{{ diffMode ? diffLevelA + '+' + diffLevelB : selectedLevel + ' / 5' }}</span>
              </div>
              <div class="footer-stat">
                <span class="stat-label">Dataset</span>
                <span class="stat-val">{{ datasetName }}</span>
              </div>
              <div class="footer-stat">
                <span class="stat-label">Richness</span>
                <span class="stat-val">{{ richnessCount }} / 5</span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
const DATASET_PATHS = [null, 'assets/shapenet/output', 'assets/PIX/output', 'assets/Omni/output', 'assets/Toys/output', 'assets/GSO2/output', 'assets/ABO/output'];
const DATASET_NAMES = ['Objaverse', 'ShapeNet', 'Pix3D', 'OmniObject3D', 'Toys4K', 'GSO', 'ABO'];

export default {
  name: 'ModelPopup',
  props: {
    visible:     { type: Boolean, required: true },
    modelData:   { type: Object,  required: true },
    type:        { type: Number,  default: 0 },
    modelIndex:  { type: Number,  default: 0 },
    totalModels: { type: Number,  default: 0 },
  },
  data() {
    return {
      selectedLevel: 4,
      viewerLoading: false,
      diffMode: false,
      diffLevelA: 4,
      diffLevelB: 1,
      copied: false,
      shared: false,
      orderedLevels: [1,2,3,4,5],
      levelShort: { 1: 'Full', 2: 'Detailed', 3: 'Semantic', 4: 'Summary', 5: 'Keywords' },
      levelMeta: {
        1: 'Comprehensive — materials, geometry, spatial relationships and context',
        2: 'Moderately detailed — key visual and functional properties',
        3: 'Functional semantic — role and purpose of the object',
        4: 'Summary — single concise sentence',
        5: 'Keywords — tag-style retrieval terms',
      },
    };
  },
  computed: {
    isMobile() { return window.innerWidth < 640; },
    datasetName() { return DATASET_NAMES[this.type] || 'Unknown'; },
    imageSrc() { const b = DATASET_PATHS[this.type]; return b ? `${b}/${this.modelData.id}.png` : ''; },
    wordCount() {
      const t = this.getText(this.selectedLevel);
      return t ? t.trim().split(/\s+/).length : 0;
    },
    richnessCount() {
      return [1,2,3,4,5].filter(l => !!this.getText(l)).length;
    },
  },
  watch: {
    visible(val) {
      document.body.style.overflow = val ? 'hidden' : '';
      if (val && this.type === 0) this.$nextTick(() => this.loadSketchfab());
    },
    modelData() {
      // Reset and reload viewer when model changes via keyboard nav
      this.selectedLevel = 4;
      if (this.visible && this.type === 0) this.$nextTick(() => this.loadSketchfab());
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey);
    if (this.visible && this.type === 0) this.$nextTick(() => this.loadSketchfab());
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
    document.body.style.overflow = '';
  },
  methods: {
    close() { this.$emit('close'); },
    onKey(e) {
      if (!this.visible) return;
      if (e.key === 'Escape')      { this.close(); }
      if (e.key === 'ArrowLeft')   { e.preventDefault(); this.$emit('prev'); }
      if (e.key === 'ArrowRight')  { e.preventDefault(); this.$emit('next'); }
      if (e.key === 'd' || e.key === 'D') { this.diffMode = !this.diffMode; }
      const num = parseInt(e.key);
      if (num >= 1 && num <= 5)    { this.selectedLevel = num; }
    },
    getText(level) {
      const map = { 1: 'level1', 2: 'level2', 3: 'level3', 4: 'level4', 5: 'level5' };
      return this.modelData[map[level]] || '';
    },
    formatName(name) {
      if (!name) return '';
      return name.replace(/[_\W]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
    },
    copyAnnotation() {
      const text = this.diffMode
        ? `Level ${this.diffLevelA}:\n${this.getText(this.diffLevelA)}\n\nLevel ${this.diffLevelB}:\n${this.getText(this.diffLevelB)}`
        : this.getText(this.selectedLevel);
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      });
    },
    shareLink() {
      const uid = this.modelData.uid || this.modelData.id;
      const url = `${window.location.origin}${window.location.pathname}?model=${uid}&dataset=${this.type}`;
      navigator.clipboard.writeText(url).then(() => {
        this.shared = true;
        setTimeout(() => { this.shared = false; }, 2000);
      });
    },
    loadSketchfab() {
      const uid = this.modelData.uid || this.modelData.id;
      if (!uid || !this.$refs.viewerContainer) return;
      this.$refs.viewerContainer.innerHTML = '';
      this.viewerLoading = true;
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'width:100%;height:100%;border:none;';
      iframe.allow = 'autoplay; fullscreen; xr-spatial-tracking';
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.src = 'about:blank';
      this.$refs.viewerContainer.appendChild(iframe);
      const init = () => {
        /* global Sketchfab */
        const client = new Sketchfab(iframe);
        client.init(uid, {
          success: (api) => { api.start(); api.addEventListener('viewerready', () => { this.viewerLoading = false; }); },
          error: () => { this.viewerLoading = false; },
          ui_theme: 'dark', ui_infos: 0, ui_watermark: 0, autospin: 0.2,
        });
      };
      if (window.Sketchfab) { init(); }
      else {
        const s = document.createElement('script');
        s.src = 'https://static.sketchfab.com/api/sketchfab-viewer-1.12.1.js';
        s.onload = init;
        document.head.appendChild(s);
      }
    },
  },
};
</script>

<style scoped>
.backdrop {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; padding: 1.5rem;
}
.panel {
  width: 100%; max-width: 960px; max-height: 90vh;
  background: var(--bg2); border: 1px solid var(--border2);
  border-radius: 20px; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 32px 80px rgba(0,0,0,0.5);
}
.fullscreen { max-width: 100%; max-height: 100%; border-radius: 0; height: 100%; }

/* Topbar */
.topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; border-bottom: 1px solid var(--border);
  background: var(--surface); flex-shrink: 0;
}
.topbar-left { display: flex; align-items: center; gap: 10px; }
.dataset-badge {
  font-family: 'JetBrains Mono', monospace; font-size: 0.62rem; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent);
  background: rgba(91,127,255,0.1); border: 1px solid rgba(91,127,255,0.25);
  padding: 3px 9px; border-radius: 100px;
}
.object-uid { font-family: 'JetBrains Mono', monospace; font-size: 0.62rem; color: var(--text3); }
.topbar-actions { display: flex; align-items: center; gap: 4px; }
.topbar-sep { width: 1px; height: 18px; background: var(--border2); margin: 0 4px; }
.nav-counter { font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; color: var(--text3); min-width: 40px; text-align: center; }

/* Icon buttons */
.icon-btn {
  width: 30px; height: 30px; border: 1px solid var(--border2);
  background: var(--surface2); border-radius: 8px; cursor: pointer;
  color: var(--text2); display: flex; align-items: center; justify-content: center;
  transition: all var(--transition);
}
.icon-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); background: rgba(91,127,255,0.08); }
.icon-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.icon-btn.active { border-color: var(--accent); color: var(--accent); background: rgba(91,127,255,0.12); }
.icon-btn.success { border-color: #28c840; color: #28c840; background: rgba(40,200,64,0.1); }
.close-btn {
  width: 30px; height: 30px; border: 1px solid var(--border2);
  background: var(--surface2); border-radius: 8px; cursor: pointer; color: var(--text2);
  display: flex; align-items: center; justify-content: center; transition: all var(--transition);
}
.close-btn:hover { background: rgba(255,60,60,0.1); border-color: rgba(255,60,60,0.35); color: #ff6060; }

/* Object header */
.object-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 18px 10px; border-bottom: 1px solid var(--border); flex-shrink: 0; gap: 12px;
}
.object-name {
  font-family: 'Google Sans', 'Syne', sans-serif;
  font-size: clamp(0.95rem, 2.5vw, 1.2rem);
  font-weight: 700; color: var(--text); line-height: 1.3; margin: 0;
}
.kbd-bar {
  display: flex; gap: 10px; flex-shrink: 0;
  font-family: 'JetBrains Mono', monospace; font-size: 0.62rem; color: var(--text3);
}
.kbd-bar span { display: flex; align-items: center; gap: 3px; }
kbd {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--surface2); border: 1px solid var(--border2);
  border-radius: 4px; padding: 1px 5px; font-family: 'JetBrains Mono', monospace;
  font-size: 0.6rem; color: var(--text2); line-height: 1.4;
}

/* Body */
.body { display: grid; grid-template-columns: 1.1fr 0.9fr; flex: 1; min-height: 0; overflow: hidden; }

/* Viewer */
.viewer-pane { position: relative; background: var(--bg); border-right: 1px solid var(--border); overflow: hidden; min-height: 360px; }
.viewer-fill { position: absolute; inset: 0; }
.static-img { width: 100%; height: 100%; object-fit: contain; display: block; }
.viewer-overlay {
  position: absolute; inset: 0; z-index: 2;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px;
  background: rgba(3,5,15,0.75); backdrop-filter: blur(4px);
  font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: var(--text3);
}
.spinner { width: 28px; height: 28px; border: 2px solid var(--border2); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.75s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Annotation pane */
.anno-pane { display: flex; flex-direction: column; padding: 16px 16px 0; min-height: 0; overflow: hidden; }
.level-header {
  display: flex; align-items: center; gap: 7px;
  font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--text3); margin-bottom: 10px; flex-shrink: 0;
}
.diff-close {
  margin-left: auto; background: none; border: none; cursor: pointer;
  color: var(--text3); font-family: 'JetBrains Mono', monospace; font-size: 0.65rem;
  transition: color var(--transition);
}
.diff-close:hover { color: var(--text); }

/* Level pills */
.level-pills { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 10px; flex-shrink: 0; }
.level-pill {
  display: flex; align-items: center; gap: 4px; padding: 5px 11px; border-radius: 100px;
  border: 1px solid var(--border2); background: rgba(255,255,255,0.03);
  color: var(--text3); font-family: 'Google Sans', sans-serif; font-size: 0.72rem; font-weight: 600;
  cursor: pointer; transition: all var(--transition); backdrop-filter: blur(8px);
}
.level-pill.sm { padding: 4px 8px; font-size: 0.68rem; }
.level-pill:hover { border-color: var(--accent); color: var(--text); background: rgba(91,127,255,0.08); }
.level-pill.active { border-color: var(--accent); background: rgba(91,127,255,0.12); color: var(--text); box-shadow: inset 0 1px 0 rgba(255,255,255,0.06); }
.pill-num { font-family: 'JetBrains Mono', monospace; font-size: 0.58rem; color: var(--accent); opacity: 0.75; }
.level-pill.active .pill-num { opacity: 1; }

/* Level strip */
.level-strip {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 11px; background: var(--surface); border: 1px solid var(--border);
  border-radius: 8px; margin-bottom: 12px; flex-shrink: 0;
}
.strip-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--accent); flex-shrink: 0; margin-top: 5px; }
.strip-text { font-family: 'JetBrains Mono', monospace; font-size: 0.67rem; color: var(--text2); line-height: 1.5; }

/* Annotation text */
.anno-scroll { flex: 1; overflow-y: auto; padding-right: 4px; min-height: 0; }
.anno-scroll::-webkit-scrollbar { width: 2px; }
.anno-scroll::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 2px; }
.anno-text p { font-family: 'Google Sans', sans-serif; font-size: 0.85rem; line-height: 1.8; color: var(--text2); margin: 0; }
.anno-empty { color: var(--text3) !important; font-style: italic; }

/* ── Diff mode ── */
.diff-selectors { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; flex-shrink: 0; }
.diff-col-label { font-family: 'JetBrains Mono', monospace; font-size: 0.62rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text3); margin-bottom: 6px; }
.diff-panels { display: grid; grid-template-columns: 1fr 1px 1fr; gap: 0; flex: 1; min-height: 0; overflow: hidden; }
.diff-divider { background: var(--border); }
.diff-panel { display: flex; flex-direction: column; overflow: hidden; }
.diff-panel:first-child { padding-right: 10px; }
.diff-panel:last-child { padding-left: 10px; }
.diff-panel-head {
  font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--accent); margin-bottom: 8px; flex-shrink: 0;
}
.diff-panel-text { overflow-y: auto; flex: 1; }
.diff-panel-text::-webkit-scrollbar { width: 2px; }
.diff-panel-text::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 2px; }
.diff-panel-text p { font-family: 'Google Sans', sans-serif; font-size: 0.78rem; line-height: 1.75; color: var(--text2); margin: 0; }

/* Footer stats */
.anno-footer {
  display: flex; justify-content: space-between;
  padding: 10px 0 14px; border-top: 1px solid var(--border); margin-top: 10px; flex-shrink: 0;
}
.footer-stat { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.stat-label { font-family: 'JetBrains Mono', monospace; font-size: 0.56rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text3); }
.stat-val { font-family: 'Google Sans', sans-serif; font-size: 0.82rem; font-weight: 700; color: var(--text); }

/* Transitions */
.modal-enter-active, .modal-leave-active { transition: opacity 0.3s ease; }
.modal-enter-active .panel, .modal-leave-active .panel { transition: transform 0.35s cubic-bezier(0.34,1.4,0.64,1), opacity 0.3s ease; }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter .panel { transform: scale(0.94) translateY(16px); opacity: 0; }
.modal-leave-to .panel { transform: scale(0.97) translateY(8px); opacity: 0; }
.slide-enter-active, .slide-leave-active { transition: all 0.2s ease; }
.slide-enter { opacity: 0; transform: translateY(8px); }
.slide-leave-to { opacity: 0; transform: translateY(-6px); }
.fade-enter-active, .fade-leave-active { transition: opacity 0.35s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .body { grid-template-columns: 1fr; grid-template-rows: 260px 1fr; overflow-y: auto; }
  .viewer-pane { border-right: none; border-bottom: 1px solid var(--border); min-height: 260px; }
  .backdrop { padding: 0; }
  .anno-pane { padding: 12px 12px 0; }
  .kbd-bar { display: none; }
}
</style>